import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import type { CustomTheme, DomainSettings } from '../../types/theme';
import { Save, AlertCircle, Check, Loader2, CheckCircle } from 'lucide-react';
import { getFunctions, httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { ColorPreview } from './ColorPreview';
import { defaultTheme } from '../../types/theme';

interface CustomizationSettingsProps {
  theme: CustomTheme;
  domain: DomainSettings;
  onUpdate: (updates: { theme?: CustomTheme; domain?: DomainSettings }) => void;
}

export function CustomizationSettings({ theme = defaultTheme, domain, onUpdate }: CustomizationSettingsProps) {
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { user } = useAuth();
  const functions = getFunctions();

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    setShowSuccess(false);
    
    try {
      await onUpdate({ theme });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save customization settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!domain.customDomain) {
      setError('Please enter a domain name');
      return;
    }
    
    setVerifying(true);
    setError('');

    try {
      const verifyDomain = httpsCallable(functions, 'verifyDomain');
      const result = await verifyDomain({ domain: domain.customDomain });
      const { verified, message } = (result.data as { verified: boolean; message: string });
      
      if (verified) {
        onUpdate({
          domain: {
            ...domain,
            verified: true,
            lastVerificationAttempt: new Date().toISOString()
          }
        });
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        throw new Error(message || 'Domain verification failed');
      }
    } catch (err: any) {
      // Handle different types of errors
      if (err.code === 'functions/invalid-argument') {
        setError('Invalid domain format. Please enter a valid domain name.');
      } else if (err.code === 'functions/permission-denied') {
        setError('Custom domains require a premium subscription.');
      } else if (err.code === 'functions/failed-precondition') {
        setError('Domain verification failed. Please check your DNS settings and ensure the CNAME record points to app.printshoppricing.com');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred during domain verification. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  };

  const colorFields = [
    { key: 'brandPrimary', label: 'Brand Primary Color', description: 'Main brand color used for key elements' },
    { key: 'brandSecondary', label: 'Brand Secondary Color', description: 'Secondary brand color for accents' },
    { key: 'buttonColor', label: 'Button Color', description: 'Color for primary buttons' },
    { key: 'linkColor', label: 'Link Color', description: 'Color for clickable links' },
    { key: 'pageBackground', label: 'Page Background', description: 'Main background color' },
    { key: 'cardBackground', label: 'Card Background', description: 'Background color for cards and components' },
    { key: 'textPrimary', label: 'Primary Text', description: 'Main text color' },
    { key: 'textSecondary', label: 'Secondary Text', description: 'Color for less prominent text' },
    { key: 'borderColor', label: 'Border Color', description: 'Color for borders and dividers' }
  ];

  return (
    <div className="space-y-8">
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4 text-red-700 dark:text-red-200 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {showSuccess && (
        <div className="rounded-md bg-green-50 dark:bg-green-900/50 p-4 text-green-700 dark:text-green-200 flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          Settings saved successfully
        </div>
      )}

      <div className="settings-section">
        <h3 className="text-lg font-medium text-default mb-6">Custom Domain</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-muted mb-2">
              Domain Name
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={domain.customDomain || ''}
                onChange={(e) => onUpdate({ 
                  domain: { ...domain, customDomain: e.target.value }
                })}
                placeholder="calculator.yourdomain.com"
                className="flex-1 rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
              />
              <button
                onClick={handleVerifyDomain}
                disabled={!domain.customDomain || verifying}
                className="btn-primary whitespace-nowrap"
              >
                {verifying ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : domain.verified ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : null}
                {domain.verified ? 'Verified' : 'Verify Domain'}
              </button>
            </div>
          </div>

          <div className="rounded-md bg-blue-50 dark:bg-blue-900/50 p-4">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              DNS Configuration Instructions
            </h4>
            <ol className="list-decimal ml-4 text-sm text-blue-700 dark:text-blue-300">
              <li>Add a CNAME record to your domain's DNS settings</li>
              <li>Point it to: <code className="font-mono">app.printshoppricing.com</code></li>
              <li>Wait for DNS propagation (may take up to 48 hours)</li>
              <li>Click "Verify Domain" to complete setup</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3 className="text-lg font-medium text-default mb-6">Theme Colors</h3>
        
        <ColorPreview theme={theme} />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {colorFields.map(({ key, label, description }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-muted mb-1">
                {label}
              </label>
              <p className="text-xs text-muted mb-2">{description}</p>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={theme[key as keyof CustomTheme]}
                  onChange={(e) => onUpdate({ 
                    theme: { ...theme, [key]: e.target.value }
                  })}
                  className="h-10 w-20"
                />
                <input
                  type="text"
                  value={theme[key as keyof CustomTheme]}
                  onChange={(e) => onUpdate({ 
                    theme: { ...theme, [key]: e.target.value }
                  })}
                  className="flex-1 rounded-md border-[var--color-border)] bg-[var(--color-bg-card)] text-default input-focus p-2"
                  placeholder="#000000"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Customization'}
        </button>
      </div>
    </div>
  );
}